import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { formatValidUntil, pluralCredits, type ProfileResponse } from '@masha/shared';
import { api } from '../lib/api';
import { Button, Card, Spinner } from '../components/ui';

export function PaymentSuccessPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const invoiceId = params.get('InvId');

  // Refresh balance — webhook должен был уже сработать к моменту, как Робокасса
  // редиректит юзера. Если не успел — повторный refetch через 2с дотянется.
  useEffect(() => {
    const refresh = () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['credits'] });
    };
    refresh();
    const t = setTimeout(refresh, 2000);
    return () => clearTimeout(t);
  }, [qc]);

  const profileQuery = useQuery<ProfileResponse>({ queryKey: ['profile'] });

  const resultQuery = useQuery({
    queryKey: ['payment-result', invoiceId],
    queryFn: () => api.getPaymentResult(invoiceId!),
    enabled: Boolean(invoiceId),
    // The webhook may land a moment after Robokassa redirects: poll until paid.
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 2000 : false),
    retry: false,
  });
  const result = resultQuery.data;

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Card variant="elevated" padding="lg" className="w-full">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-100 text-green-600 mb-4">
          <CheckCircle2 size={32} />
        </div>
        <h1 className="text-xl font-bold mb-2">Оплата прошла</h1>
        {invoiceId && <p className="text-sm text-tg-hint mb-4">Инвойс #{invoiceId}</p>}
        {result?.status === 'paid' && result.kind === 'credits' && result.credits && result.expiresAt && (
          <p className="text-sm mb-2">
            +{result.credits} {pluralCredits(result.credits)}, действуют до{' '}
            {formatValidUntil(result.expiresAt)} включительно
          </p>
        )}
        {result?.status === 'paid' && result.kind === 'turbo' && result.turboUntil && (
          <p className="text-sm mb-2">
            Турбо включён до {formatValidUntil(result.turboUntil)} включительно
          </p>
        )}
        {result?.status === 'pending' && (
          <p className="text-sm text-tg-hint mb-2">Ждём подтверждение оплаты…</p>
        )}
        {profileQuery.isLoading ? (
          <div className="my-4">
            <Spinner />
          </div>
        ) : (
          <p className="text-base mb-6">
            Текущий баланс:{' '}
            <span className="font-bold text-lg">
              {profileQuery.data?.user.balanceCredits ?? '—'}
            </span>
          </p>
        )}
        <Link to="/">
          <Button variant="primary">На главную</Button>
        </Link>
      </Card>
    </div>
  );
}
